import React from "react";

import { Grid } from "@material-ui/core";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { SaleAction } from "actions";

import { Button, SecondaryButton } from "components/Form";
import { Input, OptionTogglerField } from "components/Form/FormikControls";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import {
  getEffectiveSaleTitleBySaleId,
  getIsOnline,
  getSaleById,
} from "selectors";
import { Subtitle } from "components/Text";

interface ModalProps {
  onClose: () => void;
  saleId: number;
}

const INVOICE_TO_OPTION = {
  AGENCY: "AGENCY",
  BUYERS: "BUYERS",
};

const invoiceToOptions = [
  { value: INVOICE_TO_OPTION.AGENCY, label: "Consigned Agency" },
  { value: INVOICE_TO_OPTION.BUYERS, label: "Buyers Directly" },
];

interface FormValues {
  saleTitle: string;
  invoiceToOption: string;
}

const validationSchema = Yup.object().shape({
  saleTitle: Yup.string().required("Sale Title is required"),
});

export const Modal = ({ onClose, saleId }: ModalProps) => {
  const dispatch = useDispatch();

  const sale = useSelector(getSaleById(saleId));
  const effectiveTitle = useSelector(getEffectiveSaleTitleBySaleId(saleId));

  const isOnline = useSelector(getIsOnline);

  const { saleyard_name: saleyardName } = sale || {};

  const initialSaleTitle = `Sell Via Ex. Agt ${saleyardName}`;

  const initialValues: FormValues = {
    saleTitle: initialSaleTitle,
    invoiceToOption: INVOICE_TO_OPTION.AGENCY,
  };

  const handleSubmit = (values: FormValues) => {
    const { saleTitle, invoiceToOption } = values;

    dispatch(
      SaleAction.createSellViaExternalAgentSaleFromSale(
        saleId,
        saleTitle,
        invoiceToOption,
      ),
    );
    onClose();
  };

  return (
    <ZoomyDialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle onClose={onClose}>
        Create Sell via External Agent Sale
        <Subtitle display="flex" alignItems="center">
          {effectiveTitle}
        </Subtitle>
      </DialogTitle>
      <Formik
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        <Form>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Input
                  label="Sale Title"
                  name="saleTitle"
                  type="text"
                  tooltip="This is the sale title to be set for the sale."
                />
              </Grid>

              <Grid item xs={12}>
                <OptionTogglerField
                  label="Who should receive the invoices for this sale?"
                  name="invoiceToOption"
                  options={invoiceToOptions}
                  labelPosition="top"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={onClose}>Back</SecondaryButton>
            <Button disabled={!isOnline}>Create</Button>
          </DialogActions>
        </Form>
      </Formik>
    </ZoomyDialog>
  );
};
